import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import BalanceTrack from './BalanceTrack';
import { buildBetDisplayItems, getAnswerColor } from '../betLabels';

const clampPercent = (value) => Math.max(0, Math.min(100, value));

function BattleBalanceBar({
  choices = [],
  compact = false,
  displayItems,
  displayLabels = [],
  betUsersBySelection = {},
  roundKey,
  onOpenBetUsers,
  summaryData,
  rateData = [],
}) {
  const items = useMemo(
    () => (displayItems && displayItems.length ? displayItems : buildBetDisplayItems(choices)),
    [choices, displayItems],
  );

  const balanceData = useMemo(() => {
    const list = Array.isArray(summaryData?.betBySelection) ? summaryData.betBySelection : [];
    const totalsBySelection = {};

    list.forEach((item) => {
      const selectionIndex = Number(item?.selection);
      if (Number.isInteger(selectionIndex)) {
        totalsBySelection[selectionIndex] = Number(item.totalBet || 0);
      }
    });

    const segments = items.map((item) => ({
      ...item,
      total: Number(totalsBySelection[item.index] || 0),
    }));
    const total = segments.reduce((sum, item) => sum + item.total, 0);
    const equalPercent = segments.length > 0 ? 100 / segments.length : 0;
    const leadingTotal = Math.max(...segments.map((item) => item.total), 0);

    return {
      total,
      segments: segments.map((item) => {
        const percent = total > 0 ? clampPercent((item.total / total) * 100) : 0;
        return {
          ...item,
          percent,
          barPercent: total > 0 ? percent : equalPercent,
          isLeading: total > 0 && item.total > 0 && item.total === leadingTotal,
        };
      }),
    };
  }, [items, summaryData]);

  if (items.length < 2) {
    return null;
  }

  const snapshotKey = JSON.stringify([roundKey ?? summaryData?.round_id ?? summaryData?.round_number, items.map(({ index, choice }) => [index, choice])]);
  const track = <BalanceTrack key={snapshotKey} snapshotKey={snapshotKey} segments={balanceData.segments} total={balanceData.total} compact={compact} />;

  const baseSx = {
    p: compact ? 1 : 1.5,
    borderRadius: '8px',
    background: '#fff',
    border: '1px solid rgba(15, 23, 42, 0.10)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
  };

  const renderUserCountButton = (segment, colorSet, align = 'flex-start') => {
    if (compact) {
      return null;
    }

    const users = betUsersBySelection[segment.index] || betUsersBySelection[String(segment.index)] || [];
    if (!users.length) {
      return null;
    }

    const countLabel = users.length >= 10 ? '9+' : String(users.length);
    return (
      <Box sx={{ display: 'flex', justifyContent: align, mt: 0.5 }}>
        <Box
          component="button"
          type="button"
          onClick={() => onOpenBetUsers?.(segment)}
          sx={{
            appearance: 'none',
            border: `1px solid ${colorSet.border}`,
            bgcolor: '#fff',
            color: colorSet.primary,
            borderRadius: '999px',
            minWidth: '34px',
            height: '24px',
            px: 0.75,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 900,
            lineHeight: 1,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.10)',
            '&:hover': {
              bgcolor: colorSet.surface,
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.14)',
            },
          }}
        >
          <Box component="span">{countLabel}</Box>
          <Box component="span" sx={{ fontSize: '10px' }}>人</Box>
        </Box>
      </Box>
    );
  };

  if (items.length === 2) {
    const left = balanceData.segments[0];
    const right = balanceData.segments[1];
    const leftColor = left.color || getAnswerColor(0);
    const rightColor = right.color || getAnswerColor(1);
    const leftLabel = displayLabels[0] || left.label || left.choice;
    const rightLabel = displayLabels[1] || right.label || right.choice;

    if (!compact) {
      return (
        <Box sx={{ ...baseSx, p: 1, borderRadius: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 0.75,
              fontSize: '12px',
              color: 'rgba(15, 23, 42, 0.72)',
              fontWeight: 800,
            }}
          >
            <Box>戦況バランス</Box>
            <Box>総ベット {balanceData.total} pt</Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content minmax(0, 1fr) max-content',
              alignItems: 'center',
              gap: 1,
              fontSize: '12px',
              fontWeight: 900,
            }}
          >
            <Box sx={{ color: leftColor.primary, textAlign: 'left', whiteSpace: 'nowrap' }}>
              {left.shortLabel} {left.percent.toFixed(0)}%
              {renderUserCountButton(left, leftColor, 'flex-start')}
            </Box>
            {track}
            <Box sx={{ color: rightColor.primary, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {right.percent.toFixed(0)}% {right.shortLabel}
              {renderUserCountButton(right, rightColor, 'flex-end')}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={baseSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: compact ? 0.5 : 1,
            fontSize: '12px',
            color: 'rgba(15, 23, 42, 0.72)',
            fontWeight: 800,
          }}
        >
          <Box>戦況バランス</Box>
          <Box>総ベット {balanceData.total} pt</Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            alignItems: 'center',
            gap: 1,
            mb: compact ? 0.5 : 0.75,
            fontWeight: 800,
            fontSize: '13px',
          }}
        >
          <Box
            sx={{
              color: leftColor.primary,
              minWidth: 0,
              px: compact ? 0.5 : 0.75,
              py: compact ? 0.2 : 0.35,
              borderRadius: '10px',
              bgcolor: 'transparent',
              transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
            }}
          >
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {leftLabel}
            </Box>
            {!compact && (
              <>
                <Box sx={{ fontSize: '12px', color: 'rgba(15, 23, 42, 0.62)' }}>
                  {left.total} pt
                </Box>
                <Box sx={{ mt: 0.25, fontSize: '12px', color: leftColor.primary }}>
                  レート{rateData[0] ?? '-'}倍
                </Box>
              </>
            )}
          </Box>
          <Box sx={{ color: 'rgba(15, 23, 42, 0.58)', fontSize: '12px' }}>VS</Box>
          <Box
            sx={{
              color: rightColor.primary,
              minWidth: 0,
              textAlign: 'right',
              px: compact ? 0.5 : 0.75,
              py: compact ? 0.2 : 0.35,
              borderRadius: '10px',
              bgcolor: 'transparent',
              transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
            }}
          >
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {rightLabel}
            </Box>
            {!compact && (
              <>
                <Box sx={{ fontSize: '12px', color: 'rgba(15, 23, 42, 0.62)' }}>
                  {right.total} pt
                </Box>
                <Box sx={{ mt: 0.25, fontSize: '12px', color: rightColor.primary }}>
                  レート{rateData[1] ?? '-'}倍
                </Box>
              </>
            )}
          </Box>
        </Box>

        {track}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: compact ? 0.5 : 0.75,
            fontSize: '12px',
            fontWeight: 900,
          }}
        >
          <Box sx={{ color: leftColor.primary }}>
            {left.percent.toFixed(0)}%
          </Box>
          <Box sx={{ color: rightColor.primary }}>
            {right.percent.toFixed(0)}%
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={baseSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: compact ? 0.5 : 1,
          fontSize: '12px',
          color: 'rgba(15, 23, 42, 0.72)',
          fontWeight: 800,
        }}
      >
        <Box>戦況バランス</Box>
        <Box>総ベット {balanceData.total} pt</Box>
      </Box>

      {track}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : `repeat(${items.length}, minmax(0, 1fr))`,
          gap: 0.75,
          mt: compact ? 0.75 : 1,
        }}
      >
        {balanceData.segments.map((segment) => {
          const colorSet = segment.color || getAnswerColor(segment.index);
          const labelText = compact ? segment.shortLabel : segment.label;

          return (
            <Box
              key={segment.index}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.5,
                borderRadius: '6px',
                bgcolor: colorSet.surface,
                border: `1px solid ${colorSet.border}`,
                color: colorSet.primary,
                fontSize: '12px',
                fontWeight: 800,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {labelText}
                </Box>
                <Box sx={{ flexShrink: 0 }}>{segment.percent.toFixed(0)}%</Box>
              </Box>
              {!compact && (
                <Box sx={{ mt: 0.25, color: 'rgba(15, 23, 42, 0.62)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {segment.choice}
                </Box>
              )}
              {renderUserCountButton(segment, colorSet)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default BattleBalanceBar;
